import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { Alert, AppState, Linking, Platform, type AppStateStatus } from 'react-native';

import { useAuth } from '../../contexts/AuthContext';
import { registerJoinInviteController } from '../../core/navigation/joinInviteController';
import { WEB_HOME_PATH, isJoinPath } from '../../core/navigation/webPaths';
import {
  isInviteTokenDismissed,
  loadPendingInviteToken,
  savePendingInviteToken,
} from '../../core/storage/inviteTokenStorage';
import { getIncomingHouseholdInvite } from '../../services/householdService';
import { notifyNotificationsChanged } from '../../services/notificationsBus';
import { JoinInviteModal } from './JoinInviteModal';

type WebGlobals = {
  location?: { href: string };
  history?: { replaceState: (data: unknown, unused: string, url?: string) => void };
};

type JoinInviteGateValue = {
  /** True while we may still present (or are presenting) the join household modal. */
  isJoinInviteBlocking: boolean;
  setJoinInviteBlocking: (blocking: boolean) => void;
};

const JoinInviteGateContext = createContext<JoinInviteGateValue | null>(null);

export function JoinInviteProvider({ children }: PropsWithChildren) {
  const [isJoinInviteBlocking, setJoinInviteBlocking] = useState(true);
  const value = useMemo(
    () => ({ isJoinInviteBlocking, setJoinInviteBlocking }),
    [isJoinInviteBlocking],
  );
  return (
    <JoinInviteGateContext.Provider value={value}>{children}</JoinInviteGateContext.Provider>
  );
}

export function useJoinInviteGate(): JoinInviteGateValue {
  const ctx = useContext(JoinInviteGateContext);
  if (!ctx) {
    throw new Error('useJoinInviteGate must be used within JoinInviteProvider');
  }
  return ctx;
}

function readInviteTokenFromUrl(url?: string | null): string | null {
  try {
    if (Platform.OS === 'web') {
      const g = globalThis as WebGlobals;
      const href = url || g.location?.href;
      if (!href) return null;
      const parsed = new URL(href);
      if (isJoinPath(parsed.pathname)) {
        return parsed.searchParams.get('token');
      }
      return null;
    }

    if (!url) return null;
    const qIndex = url.indexOf('?');
    if (qIndex < 0) return null;
    const params = new URLSearchParams(url.slice(qIndex + 1));
    return params.get('token');
  } catch {
    return null;
  }
}

function stripInviteTokenFromWebUrl(): void {
  if (Platform.OS !== 'web') return;
  const g = globalThis as WebGlobals;
  const href = g.location?.href;
  if (!href || !g.history?.replaceState) return;
  try {
    const url = new URL(href);
    if (!isJoinPath(url.pathname)) return;
    url.searchParams.delete('token');
    g.history.replaceState({}, '', WEB_HOME_PATH + url.search);
  } catch {
    // ignore
  }
}

/**
 * Captures invite tokens from `/app/join?token=` (and legacy `/join`) / deep links.
 * After sign-in, also matches pending email/SMS invites so the join modal shows
 * even if they installed the app without opening the invite link.
 */
export function JoinInviteListener() {
  const { isLoading, isAuthenticated, user } = useAuth();
  const { setJoinInviteBlocking } = useJoinInviteGate();
  const [token, setToken] = useState<string | null>(() => {
    if (Platform.OS !== 'web') return null;
    const fromUrl = readInviteTokenFromUrl();
    if (fromUrl) void savePendingInviteToken(fromUrl);
    return fromUrl;
  });
  const [visible, setVisible] = useState(
    () => Platform.OS === 'web' && Boolean(readInviteTokenFromUrl()),
  );

  const presentToken = useCallback(
    async (raw: string | null | undefined) => {
      const next = raw?.trim();
      if (!next) return;
      await savePendingInviteToken(next);
      stripInviteTokenFromWebUrl();
      setToken(next);
      setVisible(true);
      setJoinInviteBlocking(true);
    },
    [setJoinInviteBlocking],
  );

  const hideJoin = useCallback(() => {
    setVisible(false);
    setJoinInviteBlocking(false);
  }, [setJoinInviteBlocking]);

  useEffect(() => {
    registerJoinInviteController({
      present: (next) => {
        void presentToken(next);
      },
    });
    return () => registerJoinInviteController(null);
  }, [presentToken]);

  const checkIncomingInvite = useCallback(async () => {
    if (!isAuthenticated) return false;
    try {
      const res = await getIncomingHouseholdInvite();
      const incoming = res.invite?.token?.trim();
      if (!incoming) return false;
      notifyNotificationsChanged();
      if (await isInviteTokenDismissed(incoming)) return false;
      await presentToken(incoming);
      return true;
    } catch {
      // Join is optional.
    }
    return false;
  }, [isAuthenticated, presentToken]);

  useEffect(() => {
    if (isLoading) return;

    let cancelled = false;

    const bootstrap = async () => {
      const fromUrl = readInviteTokenFromUrl(Platform.OS === 'web' ? undefined : null);
      if (fromUrl) {
        await presentToken(fromUrl);
        return;
      }

      const pending = await loadPendingInviteToken();
      if (pending && !(await isInviteTokenDismissed(pending))) {
        if (cancelled) return;
        setToken(pending);
        setVisible(true);
        setJoinInviteBlocking(true);
        return;
      }

      if (!isAuthenticated) {
        if (!cancelled) setJoinInviteBlocking(false);
        return;
      }

      setJoinInviteBlocking(true);
      const shown = await checkIncomingInvite();
      if (!cancelled && !shown) setJoinInviteBlocking(false);
    };

    void bootstrap();

    if (Platform.OS === 'web') {
      return () => {
        cancelled = true;
      };
    }

    const onUrl = ({ url }: { url: string }) => {
      const t = readInviteTokenFromUrl(url);
      if (t) void presentToken(t);
    };

    const sub = Linking.addEventListener('url', onUrl);
    void Linking.getInitialURL().then((url) => {
      const t = readInviteTokenFromUrl(url);
      if (t) void presentToken(t);
    });

    return () => {
      cancelled = true;
      sub.remove();
    };
  }, [checkIncomingInvite, isAuthenticated, isLoading, presentToken, setJoinInviteBlocking, user?.id]);

  useEffect(() => {
    const onChange = (status: AppStateStatus) => {
      if (status === 'active' && isAuthenticated && !isLoading) {
        void checkIncomingInvite();
      }
    };
    const sub = AppState.addEventListener('change', onChange);
    return () => sub.remove();
  }, [checkIncomingInvite, isAuthenticated, isLoading]);

  return (
    <JoinInviteModal
      visible={visible}
      token={token}
      onClose={hideJoin}
      onJoined={() => {
        notifyNotificationsChanged();
        Alert.alert(
          'Household joined',
          'Use Mine / Household on Home, Spend, and Wealth to switch views. Check Settings → Household for members.',
        );
      }}
    />
  );
}
